import Box from "@/components/Box";
import Grid from "@/components/Grid";
import { hasReportEntitlement } from "@/components/reports/reportListClick";
import { RelatedReportListCard } from "@/components/ReportListCard";
import { PanelBoxV2 } from "@/tpo/Boxes";
import Stack from "@/tpo/Stack";
import { gql, useQuery } from "@apollo/client";

const NOOP_QUERY = gql`
  query RelatedReportsSectionNoop {
    __typename
  }
`;

export default function RelatedReportsSection({
  userTest,
  reportsQuery,
  reportsQueryVariables = {},
  entitlementsQuery,
  entitlementsQueryVariables = {},
  requireReportEntitlement = false,
  skip = false,
  description,
  getReportCardState,
  onReportClick
}) {
  const { data: entitlementsData } = useQuery(entitlementsQuery || NOOP_QUERY, {
    variables: entitlementsQueryVariables,
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
    skip: skip || !entitlementsQuery
  });

  const { data: reportsData } = useQuery(reportsQuery, {
    variables: reportsQueryVariables,
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
    skip: skip || !userTest?.id
  });

  const reports = reportsData?.reports;
  const userEntitlements = entitlementsData?.user?.entitlements;
  const userReports = reportsData?.userReports?.map(userReport => userReport.subsector.id);

  if (!reports?.length) {
    return null;
  }

  if (requireReportEntitlement) {
    if (!entitlementsData || !hasReportEntitlement({ reports, userEntitlements })) {
      return null;
    }
  }

  return (
    <PanelBoxV2 maxWidth={1020} stacked gap={40}>
      <Stack gap={16}>
        <Box
          fontFamily="gilroyBold"
          fontSize={[32, 32, 44]}
          lineHeight={["36px", "36px", "50px"]}
        >
          Related reports
        </Box>
        <Box
          fontFamily="gilroyMedium"
          fontSize={[14, 14, 16]}
          lineHeight={["24px", "24px", "28px"]}
        >
          {description}
        </Box>
      </Stack>
      <Grid
        gridTemplateColumns={["repeat(1, 1fr)", "repeat(2, 1fr)", "repeat(2, 1fr)"]}
        gap={16}
        gridColumnGap={16}
      >
        {reports.map(report => {
          const { isComingSoon, isClickable, topRightButton } = getReportCardState({
            report,
            reportsData,
            userReports,
            userEntitlements
          });

          const userReport = reportsData?.userReports?.find(
            userReport => userReport.subsector.id == report.id
          );

          const userReportFunctionalDatapointCount =
            (userReport?.functionalDatapointCounts?.inRangeCount || 0) +
            (userReport?.functionalDatapointCounts?.outOfRangeCount || 0);
          const userReportDnaDatapointCount =
            (userReport?.dnaDatapointCounts?.inRangeCount || 0) +
            (userReport?.dnaDatapointCounts?.outOfRangeCount || 0);
          const userReportSymptomsCount =
            (userReport?.questionsAndAnswersCounts?.inRangeCount || 0) +
            (userReport?.questionsAndAnswersCounts?.outOfRangeCount || 0);

          const totalFunctionalDatapointCount = report?.functionalDatapointCount || 0;
          const totalDnaDatapointCount = report?.dnaDatapointCount || 0;
          const totalSymptomsCount = report?.questionsAndAnswersCount || 0;

          return (
            <RelatedReportListCard
              key={report.id}
              id={report.id}
              name={report.name}
              iconType={report?.purchaseProduct?.content?.reportIcon}
              iconSize={[40, 40, 80]}
              onClick={() => {
                if (!isClickable) return;
                onReportClick?.(report);
              }}
              functionalDatapointCount={totalFunctionalDatapointCount}
              userReportFunctionalDatapointCount={userReportFunctionalDatapointCount}
              dnaDatapointCount={totalDnaDatapointCount}
              userReportDnaDatapointCount={userReportDnaDatapointCount}
              symptomsCount={totalSymptomsCount}
              userReportSymptomsCount={userReportSymptomsCount}
              variant={isComingSoon ? "disabled" : "active"}
              topRightButton={topRightButton}
            />
          );
        })}
      </Grid>
    </PanelBoxV2>
  );
}
